import { AppContext } from "components/App/AppContext";
import { PageLink } from "components/App/utils";
import { Heading } from "components/Heading";
import { JsonForm } from "components/JsonForm";
import { Container } from "components/Layout/Container";
import { customerLogin, customerTwoFactor, userGuestAccessRequest } from "components/utils";
import { usePageTopAndBottomRows } from "components/utils/page";
import { getGuestLinkParams, getReferrerPageNumber } from "components/utils/useGuestLogin";
import { useState, useContext, ReactNode } from "react";
import { useHistory, Link, useLocation } from "react-router-dom";
import { PageComponentsList } from ".";
import { Alert, Card } from "react-bootstrap";
import { HeadingLevel } from "../../types/HeadingLevel";
import IconInputWidget from "../JsonForm/theme/widgets/IconInputWidget";
import { Icon } from "../Icon";
import { Button } from "../Button";
import { EmailButton } from "../Button/EmailButton";
import { SubmitButton } from "../Button/SubmitButton";
import Countdown from "react-countdown";
import { CountdownRendererFn } from "react-countdown";
import cn from "classnames";
import { IconName } from "@fortawesome/fontawesome-svg-core";

const COUNTDOWN_IN_MINUTES = 2;

const widgets = { iconInput: IconInputWidget };

const userNameTransformErrors = (errors: any[]) =>
    errors.map((e) => {
        let message = e.message;
        if (e.name === "required") {
            message = "Enter an email address to continue.";
        } else if (e.name === "format") {
            message = "Enter a valid email address to continue.";
        }

        return { ...e, message };
    });

const userNameSchema = {
    type: "object",
    required: ["userName"],
    properties: {
        userName: {
            type: "string",
            title: "Email",
            format: "email",
        },
    },
};

const userNameUiSchema = {
    userName: {
        "ui:widget": "iconInput",
        "ui:options": { icon: "envelope", inputType: "email", autoComplete: "username" },
        "ui:autofocus": true,
        "ui:placeholder": "email@example.com",
    },
};

const guestEmailUiSchema = {
    userName: {
        ...userNameUiSchema.userName,
        "ui:options": { ...userNameUiSchema.userName["ui:options"], autoComplete: "email" },
    },
};

const passwordSchema = {
    type: "object",
    required: ["password"],
    properties: {
        password: {
            type: "string",
            title: "Password",
        },
    },
};

const twoFactorSchema = {
    type: "object",
    required: ["password", "authCode"],
    properties: {
        password: {
            type: "string",
            title: "Password",
        },
        authCode: {
            type: "string",
            title: "Authorization Code",
        },
    },
};

const passwordUiSchema = {
    password: {
        "ui:widget": "iconInput",
        "ui:options": { icon: "lock", inputType: "password", autoComplete: "current-password" },
        "ui:autofocus": true,
        "ui:placeholder": "Enter your password",
    },
};

export function SignInPage() {
    const history = useHistory();
    const location = useLocation<{ referrer: string }>();
    const { customerNumber, authCode } = getGuestLinkParams(location?.state?.referrer);
    const isGuestLink = Boolean(customerNumber && authCode);
    const [isGuestMode, setIsGuestMode] = useState(isGuestLink);
    const [userName, setUserName] = useState("");
    const [requireTwoFactor, setRequireTwoFactor] = useState(false);
    const [guestAccessRequested, setGuestAccessRequested] = useState(false);
    const [resendDeadline, setResendDeadline] = useState(0);

    const onResetPageState = () => {
        setIsGuestMode(false);
        setUserName("");
        setRequireTwoFactor(false);
        // Drop the dead guest link referrer so it is not reused as a post-login redirect target
        if (location?.state?.referrer?.startsWith(PageLink.CustomerGuestLogin)) {
            history.replace({ search: location.search, state: undefined });
        }
    };

    const onToggleRegularToGuest = (toggle: boolean) => {
        setIsGuestMode(toggle);
        setUserName("");
        setRequireTwoFactor(false);
    };

    const onGuestAccessRequested = (email: string) => {
        setUserName(email);
        setGuestAccessRequested(true);
        setResendDeadline(Date.now() + 1000 * 60 * COUNTDOWN_IN_MINUTES);
    };

    if (!isGuestMode) {
        if (userName) {
            return <PasswordStep userName={userName} requireTwoFactor={requireTwoFactor} onEditEmail={onResetPageState} />;
        }

        return (
            <EmailStep
                onEmailFound={(email, twoFactorRequired) => {
                    setRequireTwoFactor(twoFactorRequired);
                    setUserName(email);
                }}
                onContinueAsGuest={() => onToggleRegularToGuest(true)}
            />
        );
    }

    // GuestLoginPage exchanges the code itself and only redirects here once the link is dead
    if (isGuestLink && !guestAccessRequested) {
        return <GuestLinkExpiredStep userName={userName} onBack={onResetPageState} onRequested={onGuestAccessRequested} />;
    }

    if (guestAccessRequested) {
        return (
            <GuestCheckEmailStep
                userName={userName}
                resendDeadline={resendDeadline}
                onEditEmail={() => setGuestAccessRequested(false)}
                onResent={onGuestAccessRequested}
            />
        );
    }

    return <GuestEmailStep userName={userName} onBack={() => onToggleRegularToGuest(false)} onRequested={onGuestAccessRequested} />;
}

const CreateAccountLink = ({ label }: { label: string }) => {
    const location = useLocation<{ referrer: string }>();
    return (
        <small className="d-flex justify-content-center gap-2 text-secondary mt-4">
            {label}
            <Link className="text-portal-primary text-decoration-none" to={{ pathname: PageLink.AccountCreate, state: location?.state }}>
                Create Account
            </Link>
        </small>
    );
};

type EmailStepProps = {
    onEmailFound: (userName: string, twoFactorRequired: boolean) => void;
    onContinueAsGuest: () => void;
};

const EmailStep = ({ onEmailFound, onContinueAsGuest }: EmailStepProps) => {
    const { programNumber } = useContext(AppContext);
    const [extraErrors, setExtraErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = ({ formData }: any) => {
        setExtraErrors({});
        setIsSubmitting(true);

        customerTwoFactor(formData?.userName, programNumber)
            .then((response) => {
                onEmailFound(formData?.userName, response.twoFactorRequired === true);
            })
            .catch(() => {
                setExtraErrors({
                    userName: {
                        __errors: ["The email you entered cannot be found."],
                    },
                });
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <PageWrapper>
            <CardWrapper>
                <ContainerWrapper>
                    <Heading level={HeadingLevel.H5} className="fw-medium m-0">
                        Sign In
                    </Heading>
                    <p className="text-secondary mt-1">Enter your credentials to continue.</p>
                </ContainerWrapper>
                <ContainerWrapper>
                    <JsonForm
                        formName="Sign in Email"
                        schema={userNameSchema}
                        uiSchema={userNameUiSchema}
                        onSubmit={onSubmit}
                        extraErrors={extraErrors}
                        isSubmitting={isSubmitting}
                        submitButtonText="Continue"
                        buttonClassName="d-grid mt-3"
                        widgets={widgets}
                        transformErrors={userNameTransformErrors}
                    />
                    <CreateAccountLink label="Not registered yet?" />
                </ContainerWrapper>
            </CardWrapper>
            <div className="d-flex align-items-center my-3">
                <hr className="flex-grow-1 text-secondary" />
                <small className="px-3 text-secondary fw-bold">Or</small>
                <hr className="flex-grow-1 text-secondary" />
            </div>
            <CardWrapper>
                <ContainerWrapper center>
                    <Heading level={HeadingLevel.H6} className="fw-medium m-0">
                        Sign In as Guest
                    </Heading>
                    <p className="text-secondary text-center mt-1">Continue without creating an account</p>
                    <Button variant="outline-primary" onClick={onContinueAsGuest}>
                        <Icon icon={["far", "id-badge"]} />
                        <span className="mx-2">Continue as a guest</span>
                        <IconWrapper icon="arrow-right" />
                    </Button>
                </ContainerWrapper>
            </CardWrapper>
        </PageWrapper>
    );
};

type PasswordStepProps = {
    userName: string;
    requireTwoFactor: boolean;
    onEditEmail: () => void;
};

const PasswordStep = ({ userName, requireTwoFactor, onEditEmail }: PasswordStepProps) => {
    const history = useHistory();
    const location = useLocation<{ referrer: string }>();
    const [extraErrors, setExtraErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);

    const onSubmit = ({ formData }: any) => {
        setExtraErrors({});
        setIsSubmitting(true);

        customerLogin({ userName, ...formData })
            .then(() => {
                const referrer = location?.state?.referrer;
                // The guest link referrer is not a valid redirect target - its one-time code is already dead
                if (referrer && !referrer.startsWith(PageLink.CustomerGuestLogin)) {
                    history.push(referrer);
                } else {
                    history.push(PageLink.CustomerHome);
                }
            })
            .catch(() => {
                setIsSubmitting(false);

                const message = requireTwoFactor
                    ? "The password or authorization code you entered is not correct."
                    : "The email/password combination you entered cannot be found";

                setExtraErrors({
                    password: {
                        __errors: [message],
                    },
                    authCode: {
                        __errors: [message],
                    },
                });
            });
    };

    return (
        <PageWrapper>
            <CardWrapper>
                <ContainerWrapper center>
                    <Heading level={HeadingLevel.H5} className="fw-medium m-0 mb-2">
                        Enter your password
                    </Heading>
                    <EmailButton email={userName} onClick={onEditEmail} className="mb-4" />
                </ContainerWrapper>
                <ContainerWrapper>
                    <JsonForm
                        formName="Sign in Password"
                        schema={requireTwoFactor ? twoFactorSchema : passwordSchema}
                        uiSchema={passwordUiSchema}
                        onSubmit={onSubmit}
                        extraErrors={extraErrors}
                        submitButtonText="Sign in"
                        buttonClassName="d-flex mt-3 align-items-center justify-content-between"
                        widgets={widgets}
                    >
                        <Link className="ms-3 fs-7 text-portal-primary text-decoration-none" to={PageLink.ForgotPassword}>
                            Forgot Password?
                        </Link>
                        <SubmitButton type="submit" variant="primary" isSubmitting={isSubmitting} spinnerText="Loading...">
                            Sign in
                        </SubmitButton>
                    </JsonForm>
                </ContainerWrapper>
            </CardWrapper>
        </PageWrapper>
    );
};

const useGuestAccessRequest = () => {
    const location = useLocation<{ referrer: string }>();
    const { programNumber } = useContext(AppContext);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const requestAccess = (userName: string) => {
        setIsSubmitting(true);
        const pageNumber = getReferrerPageNumber(location?.state?.referrer);

        return userGuestAccessRequest({ userName, programNumber, pageNumber }).finally(() => {
            setIsSubmitting(false);
        });
    };

    return { requestAccess, isSubmitting };
};

type GuestEmailFormProps = {
    formName: string;
    uiSchema: object;
    submitButtonText: string;
    buttonClassName: string;
    userName: string;
    onRequested: (userName: string) => void;
};

const GuestEmailForm = ({ formName, uiSchema, submitButtonText, buttonClassName, userName, onRequested }: GuestEmailFormProps) => {
    const { requestAccess, isSubmitting } = useGuestAccessRequest();
    const [extraErrors, setExtraErrors] = useState({});

    const onSubmit = ({ formData }: any) => {
        setExtraErrors({});

        requestAccess(formData.userName)
            .then(() => {
                onRequested(formData.userName);
            })
            .catch(() => {
                setExtraErrors({
                    userName: {
                        __errors: ["We couldn't send the link. Please check the email address and try again."],
                    },
                });
            });
    };

    return (
        <JsonForm
            formName={formName}
            schema={userNameSchema}
            uiSchema={uiSchema}
            onSubmit={onSubmit}
            extraErrors={extraErrors}
            isSubmitting={isSubmitting}
            submitButtonText={submitButtonText}
            buttonClassName={buttonClassName}
            widgets={widgets}
            transformErrors={userNameTransformErrors}
            formData={{ userName }}
        />
    );
};

type GuestRequestStepProps = {
    userName: string;
    onBack: () => void;
    onRequested: (userName: string) => void;
};

const GuestLinkExpiredStep = ({ userName, onBack, onRequested }: GuestRequestStepProps) => (
    <PageWrapper>
        <CardWrapper>
            <Button onClick={onBack} variant="ghost" className="mb-4 text-portal-primary">
                <IconWrapper icon="arrow-left" className="me-2" />
                Back to Sign In
            </Button>
            <ContainerWrapper center>
                <Icon icon={["far", "link"]} className="fs-1 text-secondary" />
                <Heading level={HeadingLevel.H5} className="fw-medium m-0 mt-2">
                    Your login link has expired
                </Heading>
                <p className="text-secondary text-center mt-1">
                    The link you used is no longer valid. Request a new one to continue as a guest.
                </p>
            </ContainerWrapper>
            <ContainerWrapper>
                <GuestEmailForm
                    formName="Request new guest link"
                    uiSchema={guestEmailUiSchema}
                    submitButtonText="Send new link"
                    buttonClassName="text-center mt-3"
                    userName={userName}
                    onRequested={onRequested}
                />
            </ContainerWrapper>
        </CardWrapper>
    </PageWrapper>
);

const GuestEmailStep = ({ userName, onBack, onRequested }: GuestRequestStepProps) => (
    <PageWrapper>
        <CardWrapper>
            <Button onClick={onBack} variant="ghost" className="mb-4 text-portal-primary">
                <IconWrapper icon="arrow-left" className="me-2" />
                Back to Sign In
            </Button>
            <ContainerWrapper center>
                <Icon icon={["far", "id-badge"]} className="fs-1 text-secondary" />
                <Heading level={HeadingLevel.H5} className="fw-medium m-0 mt-2">
                    Continue as Guest
                </Heading>
                <p className="text-secondary text-center mt-1">We'll send you a link to verify your guest access.</p>
            </ContainerWrapper>
            <ContainerWrapper>
                <GuestEmailForm
                    formName="Sign in Guest Email"
                    uiSchema={guestEmailUiSchema}
                    submitButtonText="Request Access"
                    buttonClassName="d-grid mt-3"
                    userName={userName}
                    onRequested={onRequested}
                />
                <CreateAccountLink label="or" />
            </ContainerWrapper>
        </CardWrapper>
    </PageWrapper>
);

type GuestCheckEmailStepProps = {
    userName: string;
    resendDeadline: number;
    onEditEmail: () => void;
    onResent: (userName: string) => void;
};

const GuestCheckEmailStep = ({ userName, resendDeadline, onEditEmail, onResent }: GuestCheckEmailStepProps) => {
    const { requestAccess, isSubmitting } = useGuestAccessRequest();
    const [showTimer, setShowTimer] = useState(true);
    const [resendFailed, setResendFailed] = useState(false);

    const onResend = () => {
        setResendFailed(false);

        requestAccess(userName)
            .then(() => {
                onResent(userName);
                setShowTimer(true);
            })
            .catch(() => {
                setResendFailed(true);
            });
    };

    const countdownRenderer: CountdownRendererFn = ({ minutes, seconds }) => (
        <>
            {minutes}:{seconds < 10 ? `0${seconds}` : seconds}
        </>
    );

    return (
        <PageWrapper>
            <CardWrapper>
                <ContainerWrapper center>
                    <EmailButton email={userName} onClick={onEditEmail} className="mb-4" isGuest />
                    <Icon icon={["far", "envelope-open-text"]} className="fs-1 text-secondary" />
                    <Heading level={HeadingLevel.H5} className="fw-medium m-0 mt-2">
                        Check your email
                    </Heading>
                    <p className="text-secondary mt-1 text-center">
                        We've sent a link to access your guest session.
                        <br />
                        It should arrive soon.
                    </p>
                </ContainerWrapper>
                <ContainerWrapper>
                    {showTimer && (
                        <Alert variant="info" className="border-0 py-2 text-center m-0">
                            <small>
                                <IconWrapper icon="link" className="me-2" />
                                Your link is active.{" "}
                                <strong>
                                    Resend a new one in{" "}
                                    <Countdown date={resendDeadline} renderer={countdownRenderer} onComplete={() => setShowTimer(false)} />
                                </strong>
                            </small>
                        </Alert>
                    )}
                    {!showTimer && (
                        <small className="d-flex align-items-center justify-content-center gap-2 text-secondary mt-2">
                            Didn’t receive a link?
                            <Button
                                onClick={onResend}
                                variant="link"
                                className="p-0 link-underline link-underline-opacity-0 link-underline-opacity-100-hover"
                                disabled={isSubmitting}
                            >
                                <small>{isSubmitting ? "Sending..." : "Resend it"}</small>
                            </Button>
                        </small>
                    )}
                    {resendFailed && (
                        <small className="d-block text-center text-danger mt-2">We couldn't resend the link. Please try again.</small>
                    )}
                </ContainerWrapper>
            </CardWrapper>
        </PageWrapper>
    );
};

const PageWrapper = ({ children, className }: { children: ReactNode; className?: string }) => {
    const { topRows, bottomRows } = usePageTopAndBottomRows();
    return (
        <>
            <PageComponentsList components={topRows} />
            <Container style={{ maxWidth: 480 }} className={cn("mt-6 mb-6", className)}>
                {children}
            </Container>
            <PageComponentsList components={bottomRows} />
        </>
    );
};

const CardWrapper = ({ children }: { children: ReactNode }) => (
    <Card className="p-2 rounded-top-4 rounded-bottom-4 border-0 shadow-sm">
        <Card.Body>{children}</Card.Body>
    </Card>
);

const ContainerWrapper = ({ center, children }: { center?: boolean; children: ReactNode }) => (
    <Container className={cn("p-0", { "d-flex flex-column align-items-center": center })}>{children}</Container>
);

const IconWrapper = ({ icon, className }: { icon: IconName; className?: string }) => <Icon icon={["fal", icon]} className={className} />;

